import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { LoadingSpinner } from '../../../components/UI';
import toast from 'react-hot-toast';

const ACTIONS = [
  { key: 'can_view',     label: 'View',     color: 'text-blue-600' },
  { key: 'can_add',      label: 'Add',      color: 'text-green-600' },
  { key: 'can_edit',     label: 'Edit',     color: 'text-yellow-600' },
  { key: 'can_delete',   label: 'Delete',   color: 'text-red-600' },
  { key: 'can_approve',  label: 'Approve',  color: 'text-emerald-600' },
  { key: 'can_reject',   label: 'Reject',   color: 'text-orange-600' },
  { key: 'can_block',    label: 'Block',    color: 'text-purple-600' },
  { key: 'can_download', label: 'Download', color: 'text-indigo-600' },
];

const emptyPerm = () => ({
  can_view:0, can_add:0, can_edit:0, can_delete:0,
  can_approve:0, can_reject:0, can_block:0, can_download:0
});

export default function RbacPermissions() {
  const [roles, setRoles]               = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [menus, setMenus]               = useState([]);
  const [permissions, setPermissions]   = useState({});
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    api.get('/rbac/roles').then(r => setRoles(r.roles?.filter(r => r.role_code !== 'citizen') || []));
    api.get('/rbac/menus').then(r => setMenus(r.menus || []));
  }, []);

  const loadPermissions = async (role) => {
    setSelectedRole(role);
    setPermissions({});
    setLoading(true);
    try {
      const r = await api.get(`/rbac/permissions/${role.id}`);
      const map = {};
      (r.permissions || []).forEach(p => {
        map[p.id] = {
          can_view:     p.can_view     ? 1 : 0,
          can_add:      p.can_add      ? 1 : 0,
          can_edit:     p.can_edit     ? 1 : 0,
          can_delete:   p.can_delete   ? 1 : 0,
          can_approve:  p.can_approve  ? 1 : 0,
          can_reject:   p.can_reject   ? 1 : 0,
          can_block:    p.can_block    ? 1 : 0,
          can_download: p.can_download ? 1 : 0,
        };
      });
      setPermissions(map);
    } catch { toast.error('Failed to load permissions'); }
    finally { setLoading(false); }
  };

  const toggle = (menuId, field) => {
    setPermissions(p => ({
      ...p,
      [menuId]: { ...(p[menuId] || emptyPerm()), [field]: p[menuId]?.[field] ? 0 : 1 }
    }));
  };

  const toggleRow = (menuId) => {
    const cur = permissions[menuId] || emptyPerm();
    const allOn = ACTIONS.every(a => cur[a.key]);
    setPermissions(p => ({
      ...p,
      [menuId]: Object.fromEntries(ACTIONS.map(a => [a.key, allOn ? 0 : 1]))
    }));
  };

  const toggleCol = (field) => {
    const allOn = menus.every(m => permissions[m.id]?.[field]);
    setPermissions(p => {
      const next = { ...p };
      menus.forEach(m => {
        next[m.id] = { ...(p[m.id] || emptyPerm()), [field]: allOn ? 0 : 1 };
      });
      return next;
    });
  };

  const savePermissions = async () => {
    if (!selectedRole) return toast.error('Select a role first');
    setSaving(true);
    try {
      // Include ALL menus — even unchecked ones (they'll have 0 values and be filtered)
      const permsArray = menus
        .map(m => ({ menu_id: m.id, ...(permissions[m.id] || emptyPerm()) }))
        .filter(p => ACTIONS.some(a => p[a.key] === 1));

      console.log('[Permissions] Saving:', { role_id: selectedRole.id, role_name: selectedRole.role_name, count: permsArray.length });

      const res = await api.post('/rbac/permissions', {
        role_id: selectedRole.id,
        permissions: permsArray
      });

      console.log('[Permissions] Response:', res);
      toast.success(`✅ Saved ${permsArray.length} permission(s) for ${selectedRole.role_name}`);
    } catch (err) {
      console.error('[Permissions] Error:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    finally { setSaving(false); }
  };

  const parentMenus = menus.filter(m => !m.parent_id);
  const childMenus  = (pid) => menus.filter(m => m.parent_id === pid);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🎛️ Menu Permissions</h1>
          <p className="text-sm text-gray-500 mt-1">Assign granular permissions per role</p>
        </div>
        {selectedRole && (
          <button onClick={savePermissions} disabled={saving} className="btn-primary">
            {saving ? '⏳ Saving...' : '💾 Save Permissions'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Role List */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wider">Select Role</h3>
          <div className="space-y-1.5">
            {roles.map(r => (
              <button key={r.id} onClick={() => loadPermissions(r)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  selectedRole?.id === r.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 text-gray-600 hover:bg-gray-50'
                }`}>
                {r.role_name}
                <span className="text-xs text-gray-400 ml-1">({r.role_code})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Matrix */}
        <div className="lg:col-span-3">
          {!selectedRole ? (
            <div className="card text-center py-16">
              <p className="text-5xl mb-3">🎛️</p>
              <p className="text-lg font-semibold text-gray-700">Select a role</p>
              <p className="text-sm text-gray-400 mt-1">Choose a role from the left to configure permissions</p>
            </div>
          ) : loading ? (
            <LoadingSpinner size="lg" className="py-20" />
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                <p className="font-semibold text-blue-800 text-sm">
                  Role: <span className="text-blue-600">{selectedRole.role_name}</span>
                  <span className="text-xs text-gray-400 ml-2">({selectedRole.role_code})</span>
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase min-w-[200px]">Menu</th>
                      {ACTIONS.map(a => (
                        <th key={a.key} className="text-center px-2 py-3 w-16">
                          <button className={`text-xs font-semibold ${a.color} hover:opacity-70`}
                            onClick={() => toggleCol(a.key)} title={`Toggle all ${a.label}`}>
                            {a.label}
                          </button>
                        </th>
                      ))}
                      <th className="text-center px-2 py-3 w-14 text-xs font-semibold text-gray-500">All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parentMenus.map(menu => {
                      const children = childMenus(menu.id);
                      const perm = permissions[menu.id] || emptyPerm();
                      const allOn = ACTIONS.every(a => perm[a.key]);
                      return (
                        <React.Fragment key={menu.id}>
                          <tr className="bg-gray-50/80 border-b border-gray-100">
                            <td className="px-4 py-2.5 font-semibold text-gray-800 text-sm">
                              {menu.menu_icon} {menu.menu_name}
                            </td>
                            {ACTIONS.map(a => (
                              <td key={a.key} className="text-center py-2.5">
                                <input type="checkbox"
                                  className="w-4 h-4 rounded cursor-pointer accent-blue-600"
                                  checked={perm[a.key] === 1}
                                  onChange={() => toggle(menu.id, a.key)} />
                              </td>
                            ))}
                            <td className="text-center py-2.5">
                              <button onClick={() => toggleRow(menu.id)}
                                className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                                  allOn ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                {allOn ? 'None' : 'All'}
                              </button>
                            </td>
                          </tr>
                          {children.map(child => {
                            const cp = permissions[child.id] || emptyPerm();
                            const childAllOn = ACTIONS.every(a => cp[a.key]);
                            return (
                              <tr key={child.id} className="border-b border-gray-50 hover:bg-blue-50/20">
                                <td className="pl-10 pr-4 py-2 text-gray-600 text-sm">
                                  ↳ {child.menu_icon} {child.menu_name}
                                </td>
                                {ACTIONS.map(a => (
                                  <td key={a.key} className="text-center py-2">
                                    <input type="checkbox"
                                      className="w-4 h-4 rounded cursor-pointer accent-blue-600"
                                      checked={cp[a.key] === 1}
                                      onChange={() => toggle(child.id, a.key)} />
                                  </td>
                                ))}
                                <td className="text-center py-2">
                                  <button onClick={() => toggleRow(child.id)}
                                    className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                                      childAllOn ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                                    }`}>
                                    {childAllOn ? 'None' : 'All'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-xs text-gray-400">💡 Click column header to toggle all | "All" button for row</p>
                <button onClick={savePermissions} disabled={saving} className="btn-primary">
                  {saving ? '⏳ Saving...' : '💾 Save Permissions'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}